/**
 * Main Application Entry Point for REAVERS
 *
 * This module imports all game components and exposes them globally
 * for backwards compatibility while using proper ES modules internally.
 */

import UnifiedAuthService from './scripts/unified-auth.js';
import WorldCommands from './scripts/world-commands.js';
import Fastpath from './scripts/fastpath.js';
import Shell from './scripts/shell/shell.js';
import { audioBank } from './audio/bank.js';

// [VP-1] Import gameserver-connection as an ESM module so Vite bundles +
// minifies it (was shipping ~50 KB gzipped raw via classic <script src=...>).
// Module side-effects assign window.GameServerConnection / TurnPump /
// PhaseBanner / WaitingIndicator / insertContentInHistory / logSync — keeping
// non-module consumers (clock-line.js, ch2-renderer.js, game-diagnostics.js,
// script-loader.js, terminal-fsm.js) wired identically to before.
import './scripts/episode-board-view.js';
import './scripts/gameserver-connection.js';
import { wsBase, wireExternalLinks } from './scripts/platform/platform.js';

// The socket relay base comes from the platform seam (absolute relay URL on
// the web build, location-built in the Activity). gameserver-connection.js
// cannot import it itself: suites run that file's source as a classic script.
window.GameServerConnection.config.wsProxyUrl = wsBase();

// Every <a data-external> leaves through the seam's openExternal().
wireExternalLinks(document);

// Expose globally for backwards compatibility with existing HTML
window.UnifiedAuthService = UnifiedAuthService;
window.WorldCommands = WorldCommands;
window.Fastpath = Fastpath;

// [vp-thin-client-fastpath] Decide login-redirect suppression SYNCHRONOUSLY at
// module load, BEFORE UnifiedAuthService.init() runs on DOMContentLoaded. When
// ?fastpath=1 is present the fast-path page must NOT navigate to login.html
// (that is the 0-authnav race) -- it instead waits in place for JS-auth. The
// flag is read by unified-auth's shouldRedirectToLogin().
if (Fastpath.shouldSuppressLoginRedirect()) {
    window.__REAVERS_FASTPATH_SUPPRESS_LOGIN__ = true;
    console.log('[fastpath] login redirect suppressed (?fastpath=1) -- awaiting JS-auth in place');
}
// [ch2 T-4.3] Expose the audio bank so non-module scripts (gameserver-connection,
// clock-line) can fire SFX cues (phase.banner.enter, ui.pulse). Initialization is
// lazy — init() + load() are called on the first user gesture (see below).
window.audioBank = audioBank;

// Lazy AudioBank unlock on first user gesture (Web Audio autoplay policy).
// Non-intrusive: any click / keydown / touchend anywhere will unlock and load.
// Safe to call repeatedly — init() and load() are idempotent.
(function wireAudioUnlock() {
    const unlock = async (ev) => {
        try {
            await audioBank.init(ev);
            await audioBank.load();
        } catch (err) {
            console.warn('[main] audioBank unlock failed:', err);
        }
    };
    const opts = { once: true, capture: true };
    window.addEventListener('click', unlock, opts);
    window.addEventListener('keydown', unlock, opts);
    window.addEventListener('touchend', unlock, opts);
})();

// Enhanced authentication using Unified Authentication Service
let authenticationInitialized = false;

async function initializeAuthentication() {
    if (authenticationInitialized) {
        return true; // Already initialized
    }

    try {
        if (typeof UnifiedAuthService !== 'undefined') {
            addLogEntry('Initializing unified authentication system...', 'success');

            const initialized = await UnifiedAuthService.init();
            if (initialized) {
                authenticationInitialized = true;
                addLogEntry('Unified authentication system ready', 'success');

                // Set up auth state monitoring
                UnifiedAuthService.onAuthStateChanged((user, token) => {
                    if (user) {
                        addLogEntry(`Authenticated as: ${user.email || user.uid}`, 'success');
                    } else {
                        addLogEntry('User signed out', 'warning');
                    }
                });

                return true;
            } else {
                addLogEntry('Authentication initialization failed', 'warning');
                return false;
            }
        } else {
            // Fallback - allow access but warn about missing auth
            addLogEntry('Unified auth service not available - running in development mode', 'warning');
            authenticationInitialized = true;
            return true;
        }
    } catch (error) {
        console.error('Authentication initialization error:', error);
        addLogEntry(`Authentication error: ${error.message}`, 'warning');

        // Don't block access during development/testing
        authenticationInitialized = true;
        return true;
    }
}

const elements = {
    status: document.getElementById('status'),
    log: document.getElementById('log'),
    history: document.getElementById('history'),
    input: null,
    cursor: null,
    // [FB-HUD-04] The overlay that draws the value + the caret. Resolved
    // alongside `input`/`cursor` in openCommandLine().
    mirror: null
};

// Initialize the history with welcome content only
function initializeHistoryWelcome() {
    // [2026-08-08-007-ENGINE] ORIGIN: game-state-transition. The boot banner is
    // the pre-auth surface — there is no beat, no world and no session yet, so
    // it carries neither a channel nor a cycle. `writeHtml` because the banner
    // is client-authored markup; no server text ever reaches that entry.
    const F = (typeof window !== 'undefined') ? window.TerminalFunnel : null;
    if (!F) return;
    F.writeHtml(
        `
        <h1>Welcome to REAVERS</h1>
        <p>A Dungeon Master Expansion Card</p>
    `,
        { origin: F.ORIGIN.GAME_STATE_TRANSITION, className: 'message welcome-message' }
    );
}

/* [2026-08-03-014-ENGINE stage 3] OPEN the command line for a new beat.
 *
 * ⚠️ THIS NO LONGER CREATES A NODE. The command line is permanent furniture in
 * `#command-block` (see index.html); this function renders its prompt prefix,
 * enables it and declares it OPEN. The name is kept because ~30 call sites and
 * the FSM address it, and renaming a function whose SEMANTICS changed would
 * have hidden the change rather than announced it — the docstring is where the
 * announcement belongs.
 *
 * What went away with the node, and why that is safe:
 *
 *   - The anomaly-1 reaping loop is DELETED. It swept duplicate
 *     `.active-input-line` elements because prompts were appended to `#history`
 *     and a raced transition could leave two, each with its own `id="input"`,
 *     so `getElementById('input')` bound listeners to an invisible stale one.
 *     There is now exactly ONE line and it is in the markup. The bug is not
 *     fixed here — it is unrepresentable.
 *
 *   - Typed text is PRESERVED across a re-open, where the old code froze it
 *     into history. With a permanent line pinned above the keyboard, eating
 *     what the player is mid-way through typing because a beat happened to
 *     land is a regression, not a reap.
 */
function openCommandLine() {
    const line = document.getElementById('command-line');
    if (!line) {
        // Loud: the block is in index.html, so its absence is a broken build,
        // not a state to degrade through. Returning quietly here would leave a
        // client that simply never accepts input again.
        console.error('[command-line] #command-line missing — the command block '
            + 'is static markup in index.html; the client cannot accept input');
        return;
    }

    /* ⚠️ THE PROMPT CARRIES NO PHASE. Ruled 2026-08-12 by the project owner:
     * "phase doesn't need to be in the input line, it's above."
     *
     * This OVERTURNS the wave-3 position recorded at clock-line.js:487-491 —
     * *"the prompt says what you are typing INTO, the clock says what the world
     * is doing. They are not duplicates of one another."* That distinction is
     * still true; the ruling is that it does not earn a third copy of the same
     * two digits on a screen that already shows them directly above the rule.
     *
     * WHAT THIS DOES NOT TOUCH, deliberately:
     *   - the CLOCK ROW's `[PHASE:NN]` (clock-line.js:505) — the half wave-3
     *     restored, and the half the owner explicitly kept. It is now the ONLY
     *     phase render adjacent to the prompt.
     *   - phase-banner.js — the owner was offered its removal and declined.
     *
     * Contract: CLAUDE_ENGINE.md "Clock display contract"; ruling recorded at
     * docs/Tome/decision-log/2026-08-12-the-prompt-carries-no-phase.md; item
     * docs/Tome/_feedback/hud.md FB-HUD-13.
     *
     * The prompt is therefore a bare `]` in EVERY beat mode. `window._clockMode`
     * / `window._phaseDisplay` are still maintained by the wire handler and are
     * still read by the clock row — this render simply no longer consults them.
     * Pinned by prompt-carries-no-phase.test.js. */
    const promptEl = line.querySelector('.input-prompt');
    if (promptEl) promptEl.innerHTML = ']';

    // Re-resolve the element refs against the permanent line. These are stable
    // now, but resolving here keeps the contract identical for callers that
    // relied on createNewInputLine() refreshing them.
    elements.input = line.querySelector('.input-field');
    elements.cursor = line.querySelector('.cursor');
    elements.mirror = line.querySelector('.input-mirror');

    if (elements.input) {
        elements.input.disabled = false;
        // [FB-HUD-04] Declare the mirror WIRED. Until this class is on the line
        // the textarea paints its own glyphs (see the `.caret-mirrored` rule in
        // index.html) — so a client that never got this far shows text with no
        // caret rather than a caret with no text.
        if (elements.mirror) line.classList.add('caret-mirrored');
        renderCaret();
    }

    // [vp-hardening-2026-05-09 D3] Annotate prompt so VP can locate via
    // [data-channel="prompt"]. cycle_id is the cycle the user will submit INTO.
    if (typeof window !== 'undefined' && window.__vpAnnotate) {
        const cycleId = (typeof TurnPump !== 'undefined'
            && TurnPump._state && TurnPump._state.currentCycleId)
            ? TurnPump._state.currentCycleId : null;
        window.__vpAnnotate(line, 'prompt', cycleId);
    }

    setupInputListeners();
    renderCommandBlockLock(false);

    // [2026-08-03-014-ENGINE stage 2] OPEN — declared only once the element is
    // rendered AND its listeners are bound.
    if (window.CommandLine) window.CommandLine.open();

    // [vp-hardening-2026-05-09 D4] Prompt-state snapshot at prompt creation.
    if (typeof window !== 'undefined' && window.__vpBuildPromptSnapshot
            && window.__vpPushPromptSnapshot
            && window.__VP_INSTRUMENTATION_ENABLED) {
        const ps = window.__vpBuildPromptSnapshot();
        if (ps) {
            ps.trigger = 'prompt_created';
            window.__vpPushPromptSnapshot(ps);
        }
    }

    if (elements.input) elements.input.focus();

    // The command block is OUTSIDE the scroll container now, so opening it can
    // no longer move the viewport. History scrolls on its own appends.
}

/* Swap the command block between its open and locked forms.
 *
 * `]`, the cursor and [send] leave TOGETHER and are replaced by a static
 * italic line. Deliberately not a disabled-looking input: a text field that is
 * still on screen invites a tap and then says nothing. */
function renderCommandBlockLock(locked, reason) {
    const line = document.getElementById('command-line');
    const resolving = document.getElementById('cmd-resolving');
    const overdue = document.getElementById('cmd-overdue');
    /* [2026-08-19-009-ENGINE] `reason` is OPTIONAL and defaults to the ordinary
     * form, so every pre-existing caller is unchanged AND actively clears the
     * overdue state. That direction is the load-bearing half: an unlock, a
     * suppression render and a plain re-lock all pass no reason, so the only
     * way to STAY overdue is for nothing at all to have happened -- which is
     * exactly the condition the label describes. */
    /* [2026-08-22-004-ENGINE] `not-asked` is the THIRD form, and it follows the
     * same defaulting rule as `overdue`: any caller that passes no reason gets
     * the ordinary line AND clears both special forms. Three mutually exclusive
     * states, one element slot, one mutator. */
    const notAsked = document.getElementById('cmd-not-asked');
    const isOverdue = !!locked && reason === 'overdue';
    const isNotAsked = !!locked && reason === 'not-asked';
    if (line) line.hidden = !!locked;
    if (resolving) resolving.hidden = !locked || isOverdue || isNotAsked;
    if (overdue) overdue.hidden = !isOverdue;
    if (notAsked) notAsked.hidden = !isNotAsked;
}
window.renderCommandBlockLock = renderCommandBlockLock;

// The old name. Kept because the FSM, the pump and the tests all call it.
function createNewInputLine() {
    openCommandLine();
}

// Expose createNewInputLine globally for TerminalFSM._applyMode()
window.createNewInputLine = createNewInputLine;

/* renderCaret — THE CARET FOLLOWS THE TEXT. [FB-HUD-04 / 2026-08-10-005-ENGINE]
 *
 * ── The bug ──────────────────────────────────────────────────────────────────
 * The `_` was a flex SIBLING sitting before the textarea, and it was set to
 * `display:none` the moment the field had a value. The textarea's own caret is
 * suppressed (`caret-color: transparent`). So the two reported symptoms were
 * one defect with two faces:
 *
 *   "cursor doesn't render with text input" — the instant you typed a character
 *      the only caret on screen was hidden, and the native one was already
 *      transparent. There was NO caret anywhere while typing.
 *   "cursor is misplaced in input line" — on an empty line the `_` sat in its
 *      own flex cell, one `gap` short of where the first character lands.
 *
 * A sibling can never follow the text, because it is outside the box the text
 * is in. Hiding it was not a workaround for that; it was the concession to it.
 *
 * ── The fix ──────────────────────────────────────────────────────────────────
 * The textarea keeps the keystrokes and stays the single source of truth for
 * the value and the insertion point. `.input-mirror` is painted over it and
 * re-renders that value in two halves with the `_` between them, which is the
 * shape the design previews have specified all along:
 *
 *     <span class="typed">go north<span class="cursor">_</span></span>
 *         — preview/hud-wave3/index.html:876
 *         — preview/combat-sequence-v2/index.html:803
 *
 * ── Why it splits at selectionStart and not at the end ───────────────────────
 * Drawing the caret at the end of the value would be right for the common case
 * and CONFIDENTLY WRONG the moment the player presses Left or taps mid-string:
 * a caret rendered where the insertion point is not is worse than no caret,
 * because it is believable. The insertion point is a fact the textarea already
 * holds, so it is read, never assumed.
 *
 * Two cases where the textarea's answer is not usable, both handled explicitly:
 *   - NOT FOCUSED. `selectionStart` reads 0 in some browsers on an unfocused
 *     field, which would park the caret before the text the player just typed.
 *     Unfocused resting position is END — the terminal convention and what the
 *     previews draw.
 *   - A RANGE IS SELECTED. The browser is already drawing that selection; a `_`
 *     inside it would be a second, competing claim about where input goes, so
 *     the caret is withheld until the selection collapses.
 */
function renderCaret() {
    const input = elements.input;
    const mirror = elements.mirror;
    const cursor = elements.cursor;
    if (!input || !mirror || !cursor) return;

    const head = mirror.querySelector('.input-mirror-head');
    const tail = mirror.querySelector('.input-mirror-tail');
    if (!head || !tail) return;

    const value = input.value || '';
    const focused = typeof document !== 'undefined'
        && document.activeElement === input;

    let start = value.length;
    let end = value.length;
    if (focused && typeof input.selectionStart === 'number') {
        start = input.selectionStart;
        end = typeof input.selectionEnd === 'number' ? input.selectionEnd : start;
    }
    // Clamp: a stale selection offset outlives the value it was taken against
    // (clear-then-render is a real ordering), and slicing past the end would
    // silently drop the caret off the line.
    if (!(start >= 0 && start <= value.length)) start = value.length;
    if (!(end >= start && end <= value.length)) end = start;

    head.textContent = value.slice(0, start);
    tail.textContent = value.slice(start);
    cursor.style.display = (end === start) ? 'inline' : 'none';

    // Keep the two layers registered when the field scrolls past its max-height.
    mirror.scrollTop = input.scrollTop;
    mirror.scrollLeft = input.scrollLeft;
}
window.renderCaret = renderCaret;

/* Bound ONCE PER DOCUMENT, not once per element — see the block below. */
let _selectionListenerBound = false;

// Setup event listeners for input field
function setupInputListeners() {
    /* [FB-HUD-09 / 2026-08-10-005-ENGINE] EVERY LISTENER HERE BINDS ONCE.
     *
     * ── What was wrong ───────────────────────────────────────────────────────
     * `setupInputListeners()` is called from every `openCommandLine()`, and
     * since stage 3 of [2026-08-03-014-ENGINE] the command line is PERMANENT
     * FURNITURE — the same `<textarea>` for the whole session. So the element
     * this binds to is the same element on every beat.
     *
     * The caret listeners were already guarded (`_caretBound`, FB-HUD-04). The
     * `input` / `keydown` / `blur` handlers below were NOT, so they accumulated
     * one full set PER BEAT against that one element. After N beats a single
     * keystroke ran N auto-resizes, N `renderCaret()` calls and pushed N
     * identical VP prompt snapshots; a single Enter dispatched N `processInput()`
     * calls and a single blur armed N timers.
     *
     * ⚠️ IT WAS INVISIBLE BECAUSE TWO GUARDS DOWNSTREAM ABSORBED IT.
     * `processInput()` holds `_processingInput` and returns early on empty text,
     * and it clears the field on entry — so duplicates 2..N of a submit hit one
     * of those and returned. The leak therefore never produced a double-send,
     * which is exactly why it survived: it read as "fine" at the only place
     * anyone would have looked. What it DID corrupt silently is the VP
     * prompt-snapshot record, whose REG3 assertions read a stream that had N
     * copies of every keystroke in it.
     *
     * ── Why the guard, and not "bind once at construction" ───────────────────
     * The obvious repair is to move binding out of `openCommandLine()` to a
     * one-time boot hook. That is the wrong shape here, for a reason specific to
     * why this function is re-entered per beat.
     *
     * `openCommandLine()` is THE UNLOCK. It is called once per beat because a
     * beat closing is what re-opens the prompt (`TurnPump._finishCycle()` ->
     * `createNewInputLine()`), and it re-does the things that genuinely are
     * per-beat: repaint the `]` prefix, re-resolve `elements.input/cursor/mirror`
     * against the line, re-enable the field, unhide the block, re-annotate for
     * the VP probe, focus. Binding is the ONLY thing in it that is per-element
     * rather than per-beat.
     *
     * Moving binding to a boot hook would decouple it from that re-resolve, and
     * the failure mode of getting that wrong is the worst one available: an
     * input the player can type into that dispatches NOTHING on Enter, with no
     * error anywhere. A flag ON THE ELEMENT keeps binding at the same call site
     * — so a replaced element is bound on the very next open — while binding
     * exactly once for as long as that element lives. It is idempotent AND
     * self-healing; a boot hook is neither.
     *
     * ⚠️ The `selectionchange` listener is on DOCUMENT, so an element flag is
     * the wrong axis for it (COORDINATE LAW: key a fact on its own axis). Under
     * today's permanent markup the element never changes and the two axes agree,
     * which is precisely how that one would have gone unnoticed once they
     * stopped agreeing. It gets a module-level flag of its own. */
    if (!elements.input) {
        // LOUD, and the same call as the missing-`#command-line` branch above:
        // the field is static markup, so its absence is a broken build. This
        // used to be a bare TypeError thrown from the middle of this function,
        // which aborted `openCommandLine()` before it unlocked the block.
        console.error('[command-line] .input-field missing inside #command-line '
            + '— the textarea is static markup in index.html; no listeners bound '
            + 'and the client cannot accept input');
        return;
    }

    // `selectionchange` is the only event that fires for EVERY way the
    // insertion point can move — arrows, click, drag, Home/End, undo,
    // autocomplete, IME. The element events below it are belt-and-braces for
    // older engines that scope `selectionchange` to the document's own
    // selection.
    if (!_selectionListenerBound && typeof document !== 'undefined'
            && document.addEventListener) {
        _selectionListenerBound = true;
        document.addEventListener('selectionchange', () => {
            if (elements.input && document.activeElement === elements.input) {
                renderCaret();
            }
        });
    }

    if (elements.input._listenersBound) return;
    elements.input._listenersBound = true;

    ['keyup', 'click', 'select', 'focus', 'blur', 'scroll', 'mouseup']
        .forEach((evt) => elements.input.addEventListener(evt, renderCaret));

    // Auto-resize textarea and handle cursor
    elements.input.addEventListener('input', function() {
        this.style.height = 'auto';
        this.style.height = Math.min(this.scrollHeight, 120) + 'px';
        renderCaret();
        // [vp-hardening-2026-05-09 D4] Push prompt-state on each keystroke
        // so REG3 (mid-stream typing preservation) can assert input_value +
        // caret_position survived background DOM mutations. No-op when
        // instrumentation is disabled.
        if (typeof window !== 'undefined' && window.__vpBuildPromptSnapshot
                && window.__vpPushPromptSnapshot
                && window.__VP_INSTRUMENTATION_ENABLED) {
            const ps = window.__vpBuildPromptSnapshot();
            if (ps) {
                ps.trigger = 'keystroke';
                window.__vpPushPromptSnapshot(ps);
            }
        }
    });

    // Handle Enter key
    elements.input.addEventListener('keydown', function(e) {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            processInput();
        }
    });

    // Handle mobile keyboard "Done" button
    // Uses TerminalFSM mode check instead of closure flag to prevent double-submit
    elements.input.addEventListener('blur', function(e) {
        setTimeout(() => {
            // If FSM is no longer in an input-open state, Enter was handled
            if (typeof TerminalFSM !== 'undefined' && !TerminalFSM.isInputOpen()) {
                return;
            }
            if (document.activeElement !== elements.input && elements.input && elements.input.value.trim()) {
                processInput();
            }
        }, 100);
    });
}

// Process user input
//
// [2026-07-29-014-ENGINE] Exposed on window (see the assignment below the
// function) so the HUD's [send] button submits through THE SAME path the Enter
// key uses. One submit path, deliberately: a second implementation would not
// share the `_processingInput` double-send guard, and [send] sits next to a
// soft keyboard whose "Done" already races Enter via the blur handler.
let _processingInput = false;
async function processInput() {
    const text = elements.input.value.trim();
    if (!text) {
        // THE PASS (time-slot-mechanics.md 5): an empty line while the server
        // asks this player per slot passes on the slots still free. Sent as the
        // empty action; the server states the player spent, and the line
        // closes on that statement. Anywhere else an empty line is nothing.
        if (typeof TurnPump !== 'undefined' && typeof TurnPump.askedPerSlot === 'function'
                && TurnPump.askedPerSlot()
                && typeof GameServerConnection !== 'undefined') {
            if (typeof window !== 'undefined' && window.CommandLine) window.CommandLine.close();
            if (typeof renderCommandBlockLock === 'function') renderCommandBlockLock(true);
            if (elements.input) {
                elements.input.disabled = true;
                renderCaret();
            }
            console.log('[prompt] prompt.pass slots=' + TurnPump._state.promptState.slots.join(','));
            GameServerConnection.sendGameMessage('');
        }
        return;
    }

    // Guard: prevent double-send from Enter + blur handler firing in sequence
    if (_processingInput) return;
    _processingInput = true;

    // [vp-hardening-2026-05-09 D4] Prompt-state snapshot at submit time —
    // captures the typed text + caret position the user just submitted.
    if (typeof window !== 'undefined' && window.__vpBuildPromptSnapshot
            && window.__vpPushPromptSnapshot
            && window.__VP_INSTRUMENTATION_ENABLED) {
        const ps = window.__vpBuildPromptSnapshot();
        if (ps) {
            ps.trigger = 'prompt_submitted';
            ps.submitted_text = text;
            window.__vpPushPromptSnapshot(ps);
        }
    }

    // Immediately freeze the active input line as a message (prevents flash + double-submit)
    // [Wave2 REG-4] Command echo replaces the active-input-line IN PLACE with
    // a `.command-history` node at the same DOM position. The clock is NOT
    // touched here — ClockLine.freeze({inPlace:true}) is called via
    // TurnPump.user_action so the clock stays pinned and only changes color.
    // [2026-08-03-014-ENGINE stage 2] CONSUMED. Closed unconditionally, outside
    // the `if` below: the command line is closed by the act of SUBMITTING, not
    // by successfully finding an element to freeze. If the element is already
    // gone (a reap raced the submit) the player still cannot type — reading the
    // element's absence as "leave it open" is the silent-zero.
    if (typeof window !== 'undefined' && window.CommandLine) window.CommandLine.close();

    // [2026-08-03-014-ENGINE stage 3] THE ECHO.
    //
    // The command line no longer lives in `#history`, so submitting can no
    // longer freeze it IN PLACE (REG-4's old mechanism — reclass the live node
    // to `.command-history` at the same DOM position). Instead the command
    // ECHOES: a fresh static line is APPENDED to history, and the permanent
    // command line is cleared and locked.
    //
    // The visible result is the same and the invariant behind REG-4 is intact —
    // the submitted text stays exactly where the player last saw it, and nothing
    // above it moves. What changed is that history now grows downward past a
    // fixed command block instead of the block being part of the flow.
    const currentInputLine = document.getElementById('command-line');
    if (elements.history) {
        // [BASELINE-FIXES Issue 2b] Carry the prompt's inner HTML (including any
        // `.input-prompt-bracket-close` span) onto the echoed line. CSS scopes
        // the red `]` to `.active-input-line ...` only, so the echoed copy picks
        // up the default grey automatically.
        // [Phase A B7] Slash-prefixed commands (e.g. `/dump-logs`) are admin/meta
        // and echo with a bare `]`, not the in-game `[PHASE:NN]` prefix.
        // ⚠️ [FB-HUD-13 / 2026-08-12] Both branches now produce `]` — the live
        // prompt carries no prefix at all. The branch is kept because it states
        // an intent that does not depend on today's prompt shape: an admin echo
        // is bare BY RULE, not incidentally. It is not evidence a prefix exists.
        const currentPrompt = currentInputLine
            ? currentInputLine.querySelector('.input-prompt') : null;
        const isSlashCommand = text.startsWith('/');
        const echoPromptHtml = isSlashCommand
            ? `<span class="input-prompt">]</span>`
            : (currentPrompt
                ? `<span class="input-prompt">${currentPrompt.innerHTML}</span>`
                : `<span class="input-prompt">]</span>`);

        // [vp-hardening-2026-05-09 D3] cycle_id taken from the active cycle
        // (the phase the command was submitted INTO).
        const cycleId = (typeof TurnPump !== 'undefined'
            && TurnPump._state && TurnPump._state.currentCycleId)
            ? TurnPump._state.currentCycleId : null;

        // [2026-08-08-007-ENGINE] ORIGIN: customer-input. THE PLAYER'S OWN
        // WORDS, echoed so they can see what they committed to. It never
        // crossed the wire inbound, so nobody but this client is accountable
        // for it — and it is addressed to no channel.
        //
        // ⚠️ If the SERVER ever echoes the player's words back, that is
        // `ledger-content` on ch4, not this. The distinction is provenance, not
        // appearance (client_funnel.md §3).
        //
        // `escapeHtml(text)` is unchanged and still load-bearing: `writeHtml`
        // sets innerHTML, so the player's own text is escaped at the caller
        // exactly as it was before the funnel existed.
        const F = (typeof window !== 'undefined') ? window.TerminalFunnel : null;
        const echo = F ? F.writeHtml(`${echoPromptHtml} ${escapeHtml(text)}`, {
            origin: F.ORIGIN.CUSTOMER_INPUT,
            cycleId: cycleId,
            className: 'message command-history',
            parent: elements.history
        }) : null;
        if (echo) {
            // `exportGameplayLog` reads `child.dataset.ts` to timestamp player
            // input lines — set through `dataset` (not setAttribute) so the
            // property access it uses keeps working verbatim.
            echo.dataset.ts = new Date().toISOString();
            if (typeof window !== 'undefined' && window.__vpAnnotate) {
                window.__vpAnnotate(echo, 'user_command', cycleId);
            }
        }
    }

    // Swap the block to its locked form: `]`, cursor and [send] leave together.
    if (typeof renderCommandBlockLock === 'function') renderCommandBlockLock(true);

    // Clear so the blur handler won't re-send, and so the re-opened line starts
    // empty rather than showing the command the player already sent.
    if (elements.input) {
        elements.input.value = '';
        elements.input.disabled = true;
        // [FB-HUD-04] Setting `.value` fires no `input` event, so the mirror
        // would keep painting the command that was just sent — under a hidden
        // line now, and still there when the line re-opens. The chips' pressed
        // state reads the same field, for the same reason.
        renderCaret();
        if (typeof GameServerConnection !== 'undefined') {
            GameServerConnection.noteCommandLineCleared();
        }
    }

    try {
        // Check async commands FIRST before DOM manipulation
        let commandHandled = false;

        // Check if this is a debug command
        if (text.startsWith('/debug') || text.startsWith('debug')) {
            const parts = text.split(/\s+/);
            const command = parts[1] || 'help';

            if (typeof debugCommand !== 'undefined') {
                await debugCommand(command);
                commandHandled = true;
            }
        }

        // Check if this is a /dump-logs command
        if (!commandHandled && text.startsWith('/dump-logs')) {
            const label = text.split(/\s+/)[1] || 'client';
            addLogEntry(`Dumping test logs as "${label}"...`, 'success');
            if (typeof GameDiagnostics !== 'undefined' && GameDiagnostics.dumpTestLogs) {
                const result = await GameDiagnostics.dumpTestLogs(label);
                if (result.success) {
                    addLogEntry(`Logs dumped: ${result.entries} entries -> ${result.file}`, 'success');
                    if (result.report_path) {
                        addLogEntry(`Full report: ${result.report_path}`, 'success');
                    }
                } else {
                    addLogEntry(`Log dump failed: ${result.error}`, 'warning');
                }
            } else {
                addLogEntry('GameDiagnostics not available', 'warning');
            }
            commandHandled = true;
        }

        // Check if this is a world management command
        if (!commandHandled && typeof WorldCommands !== 'undefined') {
            commandHandled = await WorldCommands.handleCommand(text);
        }

        // Check if this should be routed to GameServer
        if (!commandHandled && typeof GameServerConnection !== 'undefined') {
            // WaitingIndicator is managed by GameServerConnection actor model in GAMEPLAY
            const gameServerHandled = await GameServerConnection.processInput(text);
            if (gameServerHandled) {
                // Actor model: GameServer command in GAMEPLAY -- pump gate 4 creates
                // the next input line when the turn fully resolves. Do NOT create one here.
                // CHARACTER_CHOICE: FSM handles input line creation via transitions.
                return;
            }
        }

        // If a world/debug command was handled, create input line immediately
        // (these don't go through the turn pipeline).
        if (commandHandled) {
            // Character choice and non-input-open states: FSM handles input line creation
            if (typeof TerminalFSM !== 'undefined'
                && (TerminalFSM.mode === 'CHARACTER_CHOICE' || !TerminalFSM.isInputOpen())) {
                return;
            }
            // World commands or debug: FSM is still in an open state, create input line now
            createNewInputLine();
            return;
        }

        // [2026-08-08-007-ENGINE] ORIGIN: game-state-transition. This is the
        // pre-world command surface answering an unrecognised slash command —
        // no beat exists, so no channel and no cycle.
        const F = (typeof window !== 'undefined') ? window.TerminalFunnel : null;
        const dotsMsg = F ? F.write('', {
            origin: F.ORIGIN.GAME_STATE_TRANSITION,
            className: 'message processing-dots',
            parent: elements.history
        }) : null;

        addLogEntry('Processing unknown command...');

        let responseText = `Unknown command: "${text}". Try "/world help" for available commands.`;

        // Replace dots with response. `textContent`, not `innerHTML`: the
        // player's own text is interpolated into this string, and the old
        // innerHTML assignment made that an injection surface for nothing —
        // there is no markup in the response.
        if (dotsMsg) {
            dotsMsg.textContent = responseText;
            dotsMsg.classList.remove('processing-dots');
        }

        // Create new input line for next interaction
        createNewInputLine();

        addLogEntry('Response generated successfully', 'success');
    } finally {
        _processingInput = false;
    }
}

// [2026-07-29-014-ENGINE] See the note on processInput above: the HUD's [send]
// control calls this, it does not reimplement it.
window.processInput = processInput;

function addLogEntry(text, type = '') {
    const entry = document.createElement('div');
    entry.className = `log-entry ${type ? 'log-' + type : ''}`;
    entry.textContent = text;
    elements.log.appendChild(entry);
    elements.log.scrollTop = elements.log.scrollHeight;
}

function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

// Enhanced initialization
async function initializeGame() {
    try {
        // Initialize history first
        initializeHistoryWelcome();
        addLogEntry('Starting game initialization...', 'success');

        // Initialize authentication system first
        await initializeAuthentication();

        // Initialize world commands with authentication integration
        if (typeof WorldCommands !== 'undefined') {
            WorldCommands.init();
            addLogEntry('World Management system loaded successfully', 'success');

            // Integrate with unified auth service
            if (typeof UnifiedAuthService !== 'undefined') {
                UnifiedAuthService.onAuthStateChanged((user, token) => {
                    if (WorldCommands.state) {
                        WorldCommands.state.authToken = token;
                    }
                });
                addLogEntry('World commands integrated with unified authentication', 'success');
            }
        } else {
            addLogEntry('World Management system not available', 'warning');
        }

        // Click anywhere in history to focus the active input (mobile-friendly)
        document.getElementById('history').addEventListener('click', () => {
            const input = document.getElementById('input');
            if (input) input.focus();
        });

        // GameServer connection initialization
        if (typeof GameServerConnection !== 'undefined') {
            GameServerConnection.init();
            addLogEntry('GameServer connection system loaded successfully', 'success');
        } else {
            console.warn('GameServerConnection not loaded - gameplay disabled');
        }

        // Show ready messages
        addLogEntry('Game initialization complete!', 'success');

        if (typeof WorldCommands !== 'undefined') {
            addLogEntry('Ready for world commands: /world help', 'success');
        } else {
            addLogEntry('World commands unavailable - check console for errors', 'warning');
        }

        addLogEntry('Debug commands available: /debug status', 'success');

        // Create input line
        createNewInputLine();

        // [vp-thin-client-fastpath] Register the dev fast-path trigger LAST, so
        // WorldCommands + GameServerConnection are initialized. No-op for normal
        // users (no ?fastpath=1 param, not on the dev allowlist). When active it
        // waits for auth (immediately if a session is restored, or when the VP
        // JS-authenticates in-page) then auto-resumes into the terminal via the
        // existing /world join -> connect -> resume path.
        if (typeof Fastpath !== 'undefined') {
            Fastpath.maybeRun();
        }

        // [shell-cutover-default] Register the connect->play shell as the DEFAULT
        // post-login experience (cutover 2026-07-09 -- no flag, no fallback). It
        // overlays the Lobby / World-Create / Character-Choice / read-box screens
        // and hands off to this terminal on GAMEPLAY. Additive: gameserver-
        // connection.js is untouched (FSMEvents + call-through patches). The ONLY
        // suppressor is the VP fast-path above: Shell.isActive() yields to it, so a
        // ?fastpath=1 (or committed dev-allowlist) resume goes straight to GAMEPLAY
        // and the shell never renders over it. Registered AFTER Fastpath.maybeRun()
        // so the fast-path's flag is already set when the shell checks it.
        if (typeof Shell !== 'undefined' && Shell.init) {
            Shell.init();
        }

        // [2026-07-29-014-ENGINE] In-game HUD shell. LAST, because init() reads
        // #history (for the lock observer) and window.processInput (for
        // [send]), both of which are live only by this point. It is a no-op
        // unless the flag resolves ON (localhost by default; ?hud=1 / ?hud=0
        // to override), and when it is off it adds no class and no listeners.
        if (typeof HudShell !== 'undefined' && HudShell.init) {
            HudShell.init(window);
        }

    } catch (error) {
        console.error('Game initialization error:', error);
        addLogEntry(`Initialization error: ${error.message}`, 'warning');

        // Still create input line even if there are errors
        createNewInputLine();
    }
}

// Export initialization function
export { initializeGame, addLogEntry };

// Start initialization when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeGame);
} else {
    // DOM is already ready
    setTimeout(initializeGame, 100);
}
